'use client';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { memo } from 'react';

import { usePortalStore } from '@air/primitive-portal';
import { cn } from '@air/utils-tailwind';

export const TooltipProvider = RadixTooltip.Provider;

/** Raw Radix tooltip root/trigger, re-exported for composing a tooltip with
 * another `asChild` trigger (e.g. a popover/dropdown trigger). For the common
 * case prefer the bundled `Tooltip` component. */
export const TooltipRoot = RadixTooltip.Root;
export const TooltipTrigger = RadixTooltip.Trigger;

export const DEFAULT_TOOLTIP_DELAY_DURATION = 500;

export type TooltipContentProps = Pick<RadixTooltip.TooltipPortalProps, 'container'> &
  Pick<
    RadixTooltip.TooltipContentProps,
    'align' | 'alignOffset' | 'avoidCollisions' | 'children' | 'className' | 'side' | 'sideOffset'
  > & {
    'data-testid'?: string;
    hideArrow?: boolean;
  };

/** Styled tooltip content (portal + content + arrow). Must be rendered inside a
 * `TooltipRoot` alongside a `TooltipTrigger`. */
export const TooltipContent = memo(
  ({
    align,
    alignOffset,
    avoidCollisions,
    children,
    className,
    container,
    'data-testid': testId,
    hideArrow = false,
    side,
    sideOffset = 8,
  }: TooltipContentProps) => {
    const portal = usePortalStore((state) => state.portal);

    return (
      <RadixTooltip.Portal container={container || portal}>
        <RadixTooltip.Content
          align={align}
          alignOffset={alignOffset}
          avoidCollisions={avoidCollisions}
          className={cn(
            'z-portal min-h-[32px] rounded-4 bg-black px-2 py-1.5 font-medium text-14 text-white',
            className,
          )}
          data-testid={testId}
          side={side}
          sideOffset={sideOffset}
        >
          {children}
          {!hideArrow && <RadixTooltip.Arrow />}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    );
  },
);

TooltipContent.displayName = 'TooltipContent';

export type TooltipProps = Pick<
  RadixTooltip.TooltipProps,
  'defaultOpen' | 'delayDuration' | 'disableHoverableContent' | 'onOpenChange' | 'open'
> &
  Pick<RadixTooltip.TooltipTriggerProps, 'children'> &
  TooltipContentProps & {
    label?: RadixTooltip.TooltipContentProps['children'];
  };

export const Tooltip = memo(
  ({
    align,
    alignOffset,
    avoidCollisions,
    children,
    className,
    container,
    'data-testid': testId,
    defaultOpen,
    delayDuration = DEFAULT_TOOLTIP_DELAY_DURATION,
    disableHoverableContent = false,
    hideArrow = false,
    label,
    onOpenChange,
    open,
    side,
    sideOffset = 8,
  }: TooltipProps) => {
    if (!label) {
      return <>{children}</>;
    }

    return (
      <TooltipRoot
        defaultOpen={defaultOpen}
        delayDuration={delayDuration}
        disableHoverableContent={disableHoverableContent}
        onOpenChange={onOpenChange}
        open={open}
      >
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          align={align}
          alignOffset={alignOffset}
          avoidCollisions={avoidCollisions}
          className={className}
          container={container}
          data-testid={testId}
          hideArrow={hideArrow}
          side={side}
          sideOffset={sideOffset}
        >
          {label}
        </TooltipContent>
      </TooltipRoot>
    );
  },
);

Tooltip.displayName = 'Tooltip';
